'use client';

/**
 * Intent Composer Section
 *
 * DEPRECATED (2026-10-01) — not the live Discovery search composer.
 * Confirmed by live log tail, not assumed: a real search from Discovery
 * (mode3) calls `intent_grid`, not `intent_search` — that traffic comes
 * from `components/explorer/cards/intent/composer-card.tsx` (the
 * cards-everywhere version), not this file. This component is the OLD
 * schema-driven explorer section type, still reachable only through the
 * legacy "intent" mode (admin-only nav, `MODES["intent"]` in
 * total_matrix.py) — it still correctly calls `intent_search`, which is
 * itself a real but deprecated strategy (see its own module docstring).
 * Retire together once the legacy "intent" mode retires.
 *
 * @component_role Explorer section type `intent_composer`
 * @business_rule Text + up to 3 reference images → `intent_search` BFF.
 *   Multi-turn conversation state persists via `intentStore.conversationId`;
 *   each turn's enrichment accumulates on the server so "brown leather sofa"
 *   → "under $2k" → "more modern" compose naturally. History turns restore
 *   via `pendingRestoreQuery`.
 *
 * @user_outcome Describe what you need and/or drop reference images, click
 *   Search. Refinement happens in plain language; no separate filter UI.
 *
 * @integration_point klyde_edge /ui/data intent_search strategy.
 *   Response is shape-compatible with DiscoveryResponse plus `intent.*`
 *   and `pareto.*` extras.
 *
 * @layer presentation
 * @arch_component_type section
 * @arch_domain discovery (intent)
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Image as ImageIcon, Loader2, Plus, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { useIntentSearch, useIntentGrid } from '@/lib/api/hooks';
import { useTabStore } from '@/lib/stores/tab-store';
import { useIntentStore } from '@/lib/stores/intent-store';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { cn } from '@/lib/utils/cn';
import { processImageForObserve, isValidImageFile } from '@/lib/utils/image-processing';
import type {
  DiscoveryResponse,
  ExplorerSectionConfig,
  IntentSearchRequest,
  LabelsDict,
} from '@/lib/api/types';

// ============================================================
// Image config — mirrors discovery composer
// ============================================================
const IMAGE_CONFIG = {
  SLOTS_PER_ROW: 3,
  MAX_IMAGES: 3,
};

function generateId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 11)}`;
}

interface StagedImage {
  id: string;
  base64: string;
  thumbnail: string;
  filename: string;
  sizeKB: number;
}

// ============================================================
// Image Slot — local copy of discovery composer's pattern on
// purpose (both composers evolve independently).
// ============================================================
function ImageSlot({
  image,
  onAdd,
  onRemove,
  disabled,
}: {
  image?: StagedImage;
  onAdd: () => void;
  onRemove: () => void;
  disabled?: boolean;
}) {
  if (image) {
    return (
      <div className="relative group w-full aspect-square">
        <img
          src={image.thumbnail}
          alt={image.filename}
          className="w-full h-full object-cover rounded border border-border"
        />
        <button
          type="button"
          onClick={onRemove}
          className={cn(
            'absolute -top-1 -right-1 w-4 h-4 rounded-full',
            'bg-destructive text-destructive-foreground',
            'flex items-center justify-center',
            'opacity-0 group-hover:opacity-100 transition-opacity',
            'hover:bg-destructive/90',
          )}
        >
          <X className="w-2.5 h-2.5" />
        </button>
        <span className="absolute bottom-0 right-0 text-[8px] bg-background/80 px-0.5 rounded-tl">
          {image.sizeKB}KB
        </span>
      </div>
    );
  }

  if (disabled) {
    return <div className="w-full aspect-square" />;
  }

  return (
    <button
      type="button"
      onClick={onAdd}
      className={cn(
        'w-full aspect-square border-2 border-dashed border-muted-foreground/30 rounded',
        'flex items-center justify-center',
        'hover:border-primary hover:bg-accent/50 transition-colors',
      )}
    >
      <Plus className="w-4 h-4 text-muted-foreground" />
    </button>
  );
}

// ============================================================
// Section Component
// ============================================================
export function IntentComposerSection({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  config,
  labels: L,
}: {
  config: ExplorerSectionConfig;
  labels: LabelsDict;
}) {
  // Local input state — query + staged images.
  const [query, setQuery] = useState('');
  const [stagedImages, setStagedImages] = useState<StagedImage[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Shared state from intentStore.
  const conversationId = useIntentStore((s) => s.conversationId);
  const setConversationId = useIntentStore((s) => s.setConversationId);
  const history = useIntentStore((s) => s.history);
  const addHistoryTurn = useIntentStore((s) => s.addHistoryTurn);
  const clearConversation = useIntentStore((s) => s.clearConversation);
  const pendingRestoreQuery = useIntentStore((s) => s.pendingRestoreQuery);
  const clearPendingRestore = useIntentStore((s) => s.clearPendingRestore);
  const setResponse = useIntentStore((s) => s.setResponse);
  const setLoading = useIntentStore((s) => s.setLoading);
  const setGridResponse = useIntentStore((s) => s.setGridResponse);
  const setError = useIntentStore((s) => s.setError);

  const openTab = useTabStore((state) => state.openTab);
  const intentStrategy = useCapabilityStore((s) => s.strategyName('intent', 'search'));
  const gridStrategy = useCapabilityStore((s) => s.strategyName('intent', 'grid'));
  const intentSearch = useIntentSearch(intentStrategy ?? 'intent_search');
  const intentGrid = useIntentGrid(gridStrategy ?? 'intent_grid');
  const { SLOTS_PER_ROW, MAX_IMAGES } = IMAGE_CONFIG;

  // Apply pending restore from history click.
  useEffect(() => {
    if (pendingRestoreQuery !== null) {
      setQuery(pendingRestoreQuery);
      clearPendingRestore();
      inputRef.current?.focus();
    }
  }, [pendingRestoreQuery, clearPendingRestore]);

  // ----------------------------------------------------------
  // Image management
  // ----------------------------------------------------------
  const addImages = useCallback(
    async (files: File[]) => {
      const available = MAX_IMAGES - stagedImages.length;
      if (available <= 0) {
        toast.warning((L.composer_max_images || '{n} max').replace('{n}', String(MAX_IMAGES)));
        return;
      }

      const validFiles = files.filter(isValidImageFile);
      const filesToAdd = validFiles.slice(0, available);
      if (filesToAdd.length === 0) return;

      setIsProcessing(true);

      for (const file of filesToAdd) {
        try {
          const { base64, sizeKB } = await processImageForObserve(file);
          setStagedImages((prev) => [
            ...prev,
            {
              id: generateId(),
              base64,
              thumbnail: URL.createObjectURL(file),
              filename: file.name,
              sizeKB,
            },
          ]);
        } catch (err) {
          console.error('[intent-composer] Failed to process image:', file.name, err);
        }
      }

      setIsProcessing(false);
    },
    [stagedImages.length, MAX_IMAGES, L.composer_max_images],
  );

  const removeImage = useCallback((id: string) => {
    setStagedImages((prev) => {
      const img = prev.find((i) => i.id === id);
      if (img) URL.revokeObjectURL(img.thumbnail);
      return prev.filter((i) => i.id !== id);
    });
  }, []);

  const clearImages = useCallback(() => {
    stagedImages.forEach((img) => URL.revokeObjectURL(img.thumbnail));
    setStagedImages([]);
  }, [stagedImages]);

  // ----------------------------------------------------------
  // Drag & drop
  // ----------------------------------------------------------
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragActive(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragActive(false);
      const files = Array.from(e.dataTransfer.files);
      addImages(files);
    },
    [addImages],
  );

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    addImages(files);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // ----------------------------------------------------------
  // Search
  // ----------------------------------------------------------
  const handleSearch = useCallback(() => {
    const trimmed = query.trim();
    const hasQuery = trimmed.length > 0;
    const hasImages = stagedImages.length > 0;
    if (!hasQuery && !hasImages) return;

    setLoading(true);

    const body: IntentSearchRequest = {
      conversation_id: conversationId,
    };
    if (hasQuery) body.query = trimmed;
    if (hasImages) body.images = stagedImages.map((img) => img.base64);

    intentSearch.mutate(body, {
        onSuccess: (data: DiscoveryResponse) => {
          setConversationId(data.conversation_id);
          setResponse(data);
          setGridResponse(null);

          // Title composes query + image count (if any) — mirrors discovery pattern.
          const parts: string[] = [];
          if (hasQuery) parts.push(trimmed.slice(0, 20));
          if (hasImages) parts.push(`${stagedImages.length} ${L.history_image_suffix ?? 'img'}`);
          const title = parts.join(' + ') || (L.composer_search_button ?? 'Search');

          addHistoryTurn({
            id: `${data.conversation_id}-${data.turn}`,
            conversationId: data.conversation_id,
            query: hasQuery ? trimmed : '',
            imageCount: stagedImages.length,
            timestamp: new Date(),
            resultCount: data.products?.total_count ?? 0,
            response: data,
          });

          openTab({
            id: `intent-${data.conversation_id}`,
            type: 'intent',
            title,
            entityId: data.conversation_id,
          });

          setQuery('');
          clearImages();
        },
        onError: (error: Error) => {
          setLoading(false);
          toast.error(`${L.composer_search_failed ?? 'Search failed'}: ${error.message}`);
        },
      },
    );
  }, [
    query,
    stagedImages,
    conversationId,
    intentSearch,
    openTab,
    setResponse,
    setLoading,
    setConversationId,
    setGridResponse,
    addHistoryTurn,
    clearImages,
    L.composer_search_failed,
    L.composer_search_button,
    L.history_image_suffix,
  ]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSearch();
  };

  const handleNewSearch = () => {
    clearConversation();
    setQuery('');
    clearImages();
    inputRef.current?.focus();
  };

  const canSearch = query.trim().length > 0 || stagedImages.length > 0;
  const isLoading = intentSearch.isPending || isProcessing;
  const turnNumber = history.length + 1;
  const showTurnCounter = conversationId !== null && history.length > 0;

  return (
    <div className="flex flex-col">
      {/* Query input + drop zone */}
      <div
        className={cn('p-3 border-b border-border', dragActive && 'bg-primary/10 border-primary')}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <form onSubmit={handleSubmit}>
          <div className="relative">
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={
                dragActive
                  ? (L.composer_drop_active ?? 'Drop image…')
                  : (L.composer_placeholder ?? 'Search via Intent...')
              }
              disabled={isLoading}
              className={cn(
                'w-full pl-9 pr-9 py-2 text-sm rounded-md',
                'bg-muted border border-border',
                'focus:outline-none focus:ring-1 focus:ring-primary',
                'placeholder:text-muted-foreground',
                'disabled:opacity-50 disabled:cursor-not-allowed',
              )}
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            {isLoading && (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-primary" />
            )}
          </div>
        </form>

        {L.composer_help && (
          <p className="mt-2 text-xs text-muted-foreground">{L.composer_help}</p>
        )}
      </div>

      {/* Image staging */}
      <div
        className={cn('px-3 py-3 border-b border-border', dragActive && 'bg-primary/5')}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div
          className="grid gap-2"
          style={{ gridTemplateColumns: `repeat(${SLOTS_PER_ROW}, minmax(0, 1fr))` }}
        >
          {Array.from({ length: MAX_IMAGES }).map((_, slotIndex) => (
            <ImageSlot
              key={slotIndex}
              image={stagedImages[slotIndex]}
              onAdd={() => fileInputRef.current?.click()}
              onRemove={() => removeImage(stagedImages[slotIndex]?.id)}
              disabled={slotIndex > stagedImages.length}
            />
          ))}
        </div>

        <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
          <ImageIcon className="w-3 h-3 shrink-0" />
          <span className="truncate">
            {(L.composer_images_counter ?? '{current}/{max} images')
              .replace('{current}', String(stagedImages.length))
              .replace('{max}', String(MAX_IMAGES))}
          </span>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          multiple
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>

      {/* Search + new-search row */}
      <div className="px-3 py-3 flex items-center gap-2">
        <button
          type="button"
          onClick={handleSearch}
          disabled={!canSearch || isLoading}
          className={cn(
            'flex-1 flex items-center justify-center gap-2',
            'px-3 py-2 text-sm rounded',
            'bg-primary text-primary-foreground',
            'hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed',
          )}
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              {isProcessing
                ? (L.composer_processing ?? 'Processing…')
                : (L.composer_searching ?? 'Searching...')}
            </>
          ) : (
            <>
              <Search className="w-4 h-4" />
              {L.composer_search_button ?? 'Search'}
            </>
          )}
        </button>
        {conversationId && (
          <button
            type="button"
            onClick={handleNewSearch}
            disabled={isLoading}
            className={cn(
              'px-3 py-2 text-xs rounded border border-input',
              'text-muted-foreground hover:border-ring',
            )}
          >
            {L.composer_new_search ?? 'New'}
          </button>
        )}
      </div>

      {/* Turn counter */}
      {showTurnCounter && (
        <p className="px-3 pb-2 text-xs text-muted-foreground">
          {(L.composer_turn_of_conversation ?? 'Turn {n} of conversation').replace(
            '{n}',
            String(turnNumber),
          )}
        </p>
      )}

      {/* Dev seed — removed once schedule-grid lookup from tree click is wired */}
      <div className="px-3 pb-3">
        <button
          type="button"
          disabled={intentGrid.isPending}
          onClick={() => {
            setLoading(true);
            intentGrid.mutate(
              {
                queries: LOBBY_SEED_QUERIES,
                top_k_per_line: 5,
              },
              {
                onSuccess: (data) => {
                  setGridResponse(data);
                  toast.success(
                    `Grid loaded · ${data.lines.length} lines · ${data.intent_sid}`,
                  );
                },
                onError: (err) => {
                  setError(err);
                  toast.error(`Grid failed: ${err.message}`);
                },
              },
            );
          }}
          className={cn(
            'w-full px-2 py-1.5 text-[11px] rounded border border-dashed',
            'text-muted-foreground hover:border-ring hover:text-foreground',
            'disabled:opacity-50 disabled:cursor-not-allowed',
          )}
        >
          {intentGrid.isPending ? 'Loading grid…' : 'Seed lobby grid (dev)'}
        </button>
      </div>
    </div>
  );
}

/**
 * Fixed line SIDs from the lobby PDF ingest `IN_F8C783AB0F271968`.
 * Remove this and the seed button once PDF drop + ingest is wired.
 */
const LOBBY_SEED_QUERIES = [
  { line_sid: 'LI_72C2F76CB96DDB3C', text: 'Artie Buffet - Butter' },
  { line_sid: 'LI_9C775E22BDA83FD8', text: 'Sid Occasional Chair - Midnight Tweed - Black Metal' },
  { line_sid: 'LI_5E4138A1453504C1', text: 'Classique Pedestal Side Table - Smoked Ash' },
  { line_sid: 'LI_A93FA68DB4FC45F4', text: 'Natadora Pivot Side Table - Smoked Oak' },
  { line_sid: 'LI_3464B5A855BF08FA', text: 'Natadora Kora Marble Coffee Table - Small' },
];
